import * as THREE from 'three'

import { audioService } from '@shared/lib/audio'

import type { CrowdActionEvent, CrowdActionType, CrowdAvatar } from '../model/types'
import { randomAppearance } from '../model/appearance'

import { type BuiltAvatar } from './buildAvatarMesh'
import {
  buildCrowdAvatar,
  playQuaterniusClip,
  playQuaterniusIdle,
  playQuaterniusWalk,
  tickQuaterniusMixer,
} from './buildCrowdAvatar'

/** Platform SFX — same-origin under public/sounds/ */
const SOUND_SMASH_GLASS = '/sounds/glass_broken.mp3'
const SOUND_POOP_SPLAT = '/sounds/sound_scat.wav'
const SOUND_GRENADE = '/sounds/granate.wav'

/** Drop walker after this long with no crowd actions (viewer left / idle). */
const IDLE_TTL_MS = 10 * 60 * 1000
/** How often we scan for idle TTL expirations. */
const IDLE_PRUNE_INTERVAL_SEC = 5

interface Actor {
  data: CrowdAvatar
  mesh: BuiltAvatar
  /** слот по ширине 0..1 */
  slot: number
  lane: number
  /** направление: 1 вправо, -1 влево */
  dir: number
  speed: number
  phase: number
  homeX: number
  homeZ: number
  busy: boolean
  /** Walk as lite silhouette; only upgrade to full KayKit during glory. */
  preferLite: boolean
  /** Last crowd action (ms) — used for idle TTL eviction. */
  lastActiveAt: number
  blastVx: number
  blastVy: number
  blastVz: number
  blastSpin: number
}

interface PoopStreak {
  mesh: THREE.Mesh
  ndcX: number
  ndcY: number
  vxNdc: number
  vyNdc: number
  delay: number
  age: number
  maxLife: number
  baseScaleX: number
  baseScaleY: number
}

interface PoopProjectile {
  mesh: THREE.Object3D
  vx: number
  vy: number
  vz: number
  life: number
  phase: 'fly' | 'splat' | 'drip'
  dripT: number
  splatScaleX: number
  splatScaleY: number
  /** Screen-space drip trails (parented to scene, not crowd root). */
  streaks: PoopStreak[]
  target: THREE.Vector3
  /** Glass impact in NDC — drip animates these. */
  ndcX: number
  ndcY: number
  ndcVy: number
  ndcVx: number
}

interface BoomBit {
  mesh: THREE.Mesh
  vx: number
  vy: number
  vz: number
  life: number
  maxLife: number
}

interface HeartProjectile {
  mesh: THREE.Object3D
  vx: number
  vy: number
  vz: number
  life: number
  maxLife: number
  spin: number
  sway: number
  baseScale: number
}

interface GlassShard {
  mesh: THREE.Mesh
  vx: number
  vy: number
  vz: number
  spinX: number
  spinY: number
  spinZ: number
  life: number
  maxLife: number
}

interface GlassCrack {
  mesh: THREE.Object3D
  life: number
  maxLife: number
}

export interface CrowdEngineOptions {
  /** Нижняя полоса экрана (доля высоты), куда садится толпа. */
  bandHeightRatio?: number
  maxAvatars?: number
  /**
   * When crowd size reaches this count, EVERYONE switches to lite silhouettes.
   * Below this — all walkers stay full KayKit (glory still upgrades lite temporarily).
   */
  liteCrowdThreshold?: number
}

/** At this many people the whole crowd flips to lite mode. */
export const DEFAULT_LITE_CROWD_THRESHOLD = 30


/**
 * Three.js-сцена толпы аватаров:
 * - patrol слева↔справа без скучивания (слоты + lanes + soft separation)
 * - очередь spotlight-экшенов (пока throw_poop)
 */
export class CrowdEngine {
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene = new THREE.Scene()
  private readonly camera: THREE.PerspectiveCamera
  private readonly root = new THREE.Group()
  private readonly actors = new Map<string, Actor>()
  private readonly poops: PoopProjectile[] = []
  private readonly hearts: HeartProjectile[] = []
  private readonly glassShards: GlassShard[] = []
  private readonly glassCracks: GlassCrack[] = []
  private glassFlash: THREE.Mesh | null = null
  private raf = 0
  private running = false
  private width = 1
  private height = 1
  private clock = new THREE.Clock()
  private actionQueue: CrowdActionEvent[] = []
  private spotlight: Actor | null = null
  /** True while swapping lite → full KayKit before glory starts */
  private spotlightPreparing = false
  private spotlightT = 0
  private spotlightPhase:
    | 'approach'
    | 'hearts'
    | 'smash_arm'
    | 'smash_break'
    | 'windup'
    | 'throw'
    | 'grenade_arm'
    | 'grenade_fly'
    | 'boom'
    | 'recover' = 'approach'
  private spotlightAction: CrowdActionType = 'throw_poop'
  private grenade: THREE.Object3D | null = null
  private grenadeVx = 0
  private grenadeVy = 0
  private grenadeVz = 0
  private readonly boomBits: BoomBit[] = []
  /** Where the actor stood when the action started — grow in place, no teleport */
  private spotlightOriginX = 0
  private spotlightOriginZ = 0
  private readonly maxAvatars: number
  private readonly liteCrowdThreshold: number
  private host: HTMLElement
  private readonly gloryLight: THREE.SpotLight
  /** Streamer can mute crowd SFX from Studio overlay settings. */
  private sfxEnabled = true
  private idlePruneAccum = 0

  constructor(host: HTMLElement, opts: CrowdEngineOptions = {}) {
    this.host = host
    this.maxAvatars = opts.maxAvatars ?? 24
    this.liteCrowdThreshold = Math.max(
      1,
      opts.liteCrowdThreshold ?? DEFAULT_LITE_CROWD_THRESHOLD,
    )
    void opts.bandHeightRatio

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    })
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25))
    this.renderer.domElement.style.cssText =
      'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;'
    host.appendChild(this.renderer.domElement)

    this.camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100)
    this.scene.add(this.root)

    const hemi = new THREE.HemisphereLight(0xffffff, 0x445566, 1.2)
    this.scene.add(hemi)
    const key = new THREE.DirectionalLight(0xfff2e0, 0.9)
    key.position.set(2, 5, 4)
    this.scene.add(key)
    const fill = new THREE.DirectionalLight(0xaaccff, 0.4)
    fill.position.set(-3, 2, 2)
    this.scene.add(fill)

    void audioService.preload(SOUND_SMASH_GLASS)
    void audioService.preload(SOUND_POOP_SPLAT)
    void audioService.preload(SOUND_GRENADE)

    // Full-glory key light — only ramps up during trigger spotlight
    this.gloryLight = new THREE.SpotLight(0xffe8c8, 0, 18, 0.42, 0.55, 1)
    this.gloryLight.position.set(0, 4, 4)
    this.gloryLight.target.position.set(0, 0.6, 0.05)
    this.scene.add(this.gloryLight)
    this.scene.add(this.gloryLight.target)

    this.resize()
  }

  start(): void {
    if (this.running) return
    this.running = true
    this.clock.start()
    const loop = () => {
      if (!this.running) return
      this.raf = requestAnimationFrame(loop)
      this.tick(Math.min(this.clock.getDelta(), 0.05))
      this.renderer.render(this.scene, this.camera)
    }
    this.raf = requestAnimationFrame(loop)
  }

  stop(): void {
    this.running = false
    cancelAnimationFrame(this.raf)
  }

  destroy(): void {
    this.stop()
    for (const actor of this.actors.values()) actor.mesh.dispose()
    this.actors.clear()
    for (const p of this.poops) {
      this.disposePoop(p)
    }
    this.poops.length = 0
    for (const h of this.hearts) {
      h.mesh.removeFromParent()
      disposeObject3D(h.mesh)
    }
    this.hearts.length = 0
    this.clearGlassFx()
    this.clearGrenade()
    this.clearBoomBits()
    this.renderer.dispose()
    this.renderer.domElement.remove()
  }

  resize(): void {
    const rect = this.host.getBoundingClientRect()
    this.width = Math.max(1, rect.width)
    this.height = Math.max(1, rect.height)
    this.renderer.setSize(this.width, this.height, false)
    this.camera.aspect = this.width / this.height

    // Eye-level camera — less top-down “tilt”; feet still pinned to bottom
    this.camera.fov = 34
    this.camera.updateProjectionMatrix()
    this.camera.position.set(0, 1.05, 8.2)
    this.camera.lookAt(0, 0.42, 0.05)
    this.camera.updateMatrixWorld(true)
    this.pinFeetToBottom(10)
  }

  /**
   * Drop the whole crowd so feet (y=0) sit `marginPx` above the canvas bottom.
   * Uses root Y offset (more reliable than camera juggling).
   */
  private pinFeetToBottom(marginPx: number): void {
    const crowdZ = 0.05
    this.root.position.y = 0

    for (let i = 0; i < 3; i++) {
      this.camera.updateMatrixWorld(true)
      const feet = new THREE.Vector3(0, this.root.position.y, crowdZ)
      const ndc = feet.project(this.camera)
      const targetNdcY = -1 + (2 * marginPx) / Math.max(1, this.height)
      const err = targetNdcY - ndc.y
      if (Math.abs(err) < 0.002) break

      const dist = Math.max(
        0.5,
        this.camera.position.distanceTo(new THREE.Vector3(0, this.root.position.y, crowdZ)),
      )
      const vFov = THREE.MathUtils.degToRad(this.camera.fov)
      const visibleH = 2 * Math.tan(vFov / 2) * dist
      // err < 0 → feet too high on screen → move root down
      this.root.position.y += (err / 2) * visibleH
    }
  }

  /** Полуширина патруля по реальному краю камеры (с запасом под ширину модели). */
  private getPatrolSpan(): number {
    const crowdZ = 0.05
    this.camera.updateMatrixWorld(true)

    // Sample the visible X extent at the crowd's depth via unproject
    const anchor = new THREE.Vector3(0, this.root.position.y, crowdZ)
    const ndc = anchor.clone().project(this.camera)
    // Keep ~8% margin from each screen edge + character half-width
    const edge = 0.82
    const left = new THREE.Vector3(-edge, ndc.y, ndc.z).unproject(this.camera)
    const right = new THREE.Vector3(edge, ndc.y, ndc.z).unproject(this.camera)
    const half = Math.abs(right.x - left.x) * 0.5
    const bodyPad = 0.28
    return Math.max(0.8, half - bodyPad)
  }

  setAvatars(avatars: CrowdAvatar[]): void {
    void this.setAvatarsAsync(avatars)
  }

  private async setAvatarsAsync(avatars: CrowdAvatar[]): Promise<void> {
    const keep = new Set(avatars.slice(0, this.maxAvatars).map((a) => a.id))
    for (const [id, actor] of this.actors) {
      if (!keep.has(id)) {
        this.root.remove(actor.mesh.root)
        actor.mesh.dispose()
        this.actors.delete(id)
      }
    }

    const list = avatars.slice(0, this.maxAvatars)
    // Whole-crowd mode: below threshold = all full KayKit; at/above = all lite
    const preferLite = list.length >= this.liteCrowdThreshold
    const BATCH = preferLite ? 40 : 4

    for (let start = 0; start < list.length; start += BATCH) {
      const chunkEnd = Math.min(start + BATCH, list.length)
      const chunk = list.slice(start, chunkEnd)
      await Promise.all(
        chunk.map(async (data, chunkIndex) => {
          const index = start + chunkIndex
          const existing = this.actors.get(data.id)
          if (existing) {
            existing.data = data
            existing.preferLite = preferLite
            existing.mesh.updateName(data.displayName)
            this.assignSlot(existing, index, list.length)
            if (
              !existing.busy &&
              this.spotlight !== existing &&
              !this.spotlightPreparing
            ) {
              if (preferLite && existing.mesh.quality === 'high') {
                void this.restoreLiteMesh(existing)
              } else if (!preferLite && existing.mesh.quality === 'low') {
                void this.ensureFullMesh(existing)
              }
            }
            return
          }
          const mesh = await buildCrowdAvatar(
            data.appearance,
            data.displayName,
            'Walk',
            { lite: preferLite },
          )
          this.root.add(mesh.root)
          const actor: Actor = {
            data,
            mesh,
            slot: 0,
            lane: 0,
            dir: index % 2 === 0 ? 1 : -1,
            speed: 0.32 + (index % 7) * 0.05,
            phase: (index / Math.max(1, list.length)) * Math.PI * 2,
            homeX: 0,
            homeZ: 0,
            busy: false,
            preferLite,
            lastActiveAt: Date.now(),
            blastVx: 0,
            blastVy: 0,
            blastVz: 0,
            blastSpin: 0,
          }
          this.assignSlot(actor, index, list.length)
          mesh.root.position.set(actor.homeX, 0, actor.homeZ)
          this.actors.set(data.id, actor)
        }),
      )
      if (preferLite && chunkEnd < list.length) {
        await new Promise<void>((r) => requestAnimationFrame(() => r()))
      }
    }
  }

  enqueueAction(action: CrowdActionEvent): void {
    if (this.actionQueue.some((a) => a.id === action.id)) return
    this.actionQueue.push(action)
  }

  setSfxEnabled(enabled: boolean): void {
    this.sfxEnabled = enabled
  }

  private playCrowdSfx(src: string, volume: number): void {
    if (!this.sfxEnabled) return
    void audioService.play({ src, volume })
  }

  /** Dev/demo helper */
  throwPoopFromRandom(displayName?: string): void {
    this.enqueueRandomAction('throw_poop', displayName)
  }

  /** Dev/demo helper — grenade → everyone explodes → walk again */
  throwGrenadeFromRandom(displayName?: string): void {
    this.enqueueRandomAction('throw_grenade', displayName)
  }

  /** Dev/demo helper — hearts float above head */
  throwHeartFromRandom(displayName?: string): void {
    this.enqueueRandomAction('throw_heart', displayName)
  }

  /** Dev/demo helper — punch cracks the monitor glass */
  smashGlassFromRandom(displayName?: string): void {
    this.enqueueRandomAction('smash_glass', displayName)
  }

  private enqueueRandomAction(action: CrowdActionType, displayName?: string): void {
    const ids = [...this.actors.keys()]
    if (ids.length === 0) return
    const id = ids[Math.floor(Math.random() * ids.length)]
    if (!id) return
    const message =
      action === 'throw_grenade'
        ? '💣'
        : action === 'throw_heart'
          ? '❤️'
          : action === 'smash_glass'
            ? '💥'
            : '💩'
    this.enqueueAction({
      id: `local-${Date.now()}`,
      avatarId: id,
      displayName,
      action,
      message,
      timestamp: Date.now(),
    })
  }

  private assignSlot(actor: Actor, index: number, total: number): void {
    const lanes = total > 80 ? 6 : total > 24 ? 3 : 1
    actor.lane = index % lanes
    actor.slot = index / Math.max(1, total - 1 || 1)
    const span = this.getPatrolSpan()
    // Stagger start positions across the span + depth lanes
    actor.homeX = -span + actor.slot * span * 2 + ((index % 5) - 2) * 0.04
    const laneSpread = total >= this.liteCrowdThreshold ? 0.28 : 0.18
    actor.homeZ = (actor.lane - (lanes - 1) / 2) * laneSpread
    actor.phase = (index / Math.max(1, total)) * Math.PI * 2
    actor.speed = 0.28 + (index % 6) * 0.045 + actor.lane * 0.02
  }

  private tick(dt: number): void {
    this.tickSpotlight(dt)
    this.tickPatrol(dt)
    this.tickPoops(dt)
    this.tickHearts(dt)
    this.tickGlassFx(dt)
    this.tickBoomBits(dt)
    this.maybeStartNextAction()
    this.tickIdlePrune(dt)
    for (const actor of this.actors.values()) {
      if (actor.mesh.quality === 'high') tickQuaterniusMixer(actor.mesh.root, dt)
    }
  }

  private tickIdlePrune(dt: number): void {
    this.idlePruneAccum += dt
    if (this.idlePruneAccum < IDLE_PRUNE_INTERVAL_SEC) return
    this.idlePruneAccum = 0
    this.pruneIdleActors()
  }

  /** Remove walkers idle longer than IDLE_TTL_MS (viewer closed tab / no more actions). */
  private pruneIdleActors(): void {
    const now = Date.now()
    const expired: string[] = []
    for (const [id, actor] of this.actors) {
      if (actor.busy || this.spotlight === actor) continue
      if (now - actor.lastActiveAt >= IDLE_TTL_MS) expired.push(id)
    }
    if (expired.length === 0) return

    for (const id of expired) this.removeActor(id)

    const preferLite = this.crowdShouldBeLite(this.actors.size)
    this.applyCrowdLiteMode(preferLite)
  }

  private removeActor(id: string): void {
    const actor = this.actors.get(id)
    if (!actor) return
    if (this.spotlight === actor) return
    this.root.remove(actor.mesh.root)
    actor.mesh.dispose()
    this.actors.delete(id)
  }

  private maybeStartNextAction(): void {
    if (this.spotlight || this.spotlightPreparing || this.actionQueue.length === 0) return
    const next = this.actionQueue.shift()
    if (!next) return
    void this.startAction(next)
  }

  /**
   * Resolve/spawn the thrower, upgrade to full KayKit, then run glory phases.
   * Must await upsert — otherwise the first click for a new viewer is dropped
   * while the mesh is still loading (avatar walks with no animation).
   */
  private async startAction(next: CrowdActionEvent): Promise<void> {
    this.spotlightPreparing = true
    let actor: Actor | undefined
    try {
      if (next.avatarId) {
        const appearance = next.appearance ?? randomAppearance(next.avatarId)
        const displayName = next.displayName ?? 'Viewer'
        await this.upsertActorAsync({
          id: next.avatarId,
          displayName,
          appearance,
        })
      }

      actor = next.avatarId ? this.actors.get(next.avatarId) : undefined
      if (!actor && !next.avatarId) {
        const all = [...this.actors.values()]
        actor = all[Math.floor(Math.random() * all.length)]
      }
      if (!actor) {
        this.spotlightPreparing = false
        return
      }

      actor.lastActiveAt = Date.now()

      if (next.displayName) {
        actor.data = { ...actor.data, displayName: next.displayName }
        actor.mesh.updateName(next.displayName)
      }
      if (next.appearance) {
        actor.data = { ...actor.data, appearance: next.appearance }
      }

      actor.busy = true
      this.spotlightOriginX = actor.mesh.root.position.x
      this.spotlightOriginZ = actor.mesh.root.position.z
      this.spotlightAction = next.action

      await this.beginSpotlight(actor, next)
    } catch (err) {
      console.warn('[CrowdEngine] startAction failed', err)
      this.spotlightPreparing = false
      if (actor) actor.busy = false
    }
  }

  /** Upgrade lite crowd chibi → full KayKit, then start glory phases. */
  private async beginSpotlight(actor: Actor, _next: CrowdActionEvent): Promise<void> {
    try {
      await this.ensureFullMesh(actor)
    } catch (err) {
      console.warn('[CrowdEngine] full mesh upgrade failed', err)
    }
    if (!this.actors.has(actor.data.id)) {
      this.spotlightPreparing = false
      actor.busy = false
      return
    }

    actor.mesh.setNameVisible(true)
    this.setGloryDrawOrder(actor, true)
    playQuaterniusIdle(actor.mesh.root)
    this.gloryLight.intensity = 1.8
    this.gloryLight.position.set(this.spotlightOriginX, 4.2, this.spotlightOriginZ + 3.5)
    this.gloryLight.target.position.set(this.spotlightOriginX, 0.7, this.spotlightOriginZ)
    this.spotlight = actor
    this.spotlightT = 0
    this.spotlightPhase = 'approach'
    this.spotlightPreparing = false
  }

  /** Swap placeholder → configured KayKit (or keep if already high). */
  private async ensureFullMesh(actor: Actor): Promise<void> {
    if (actor.mesh.quality === 'high') return

    const pos = actor.mesh.root.position.clone()
    const rotY = actor.mesh.root.rotation.y
    const nameVisible = actor.mesh.nameSprite.visible

    this.root.remove(actor.mesh.root)
    actor.mesh.dispose()

    const full = await buildCrowdAvatar(
      actor.data.appearance,
      actor.data.displayName,
      'Idle',
      { lite: false },
    )
    full.root.position.copy(pos)
    full.root.rotation.y = rotY
    full.setNameVisible(nameVisible)
    this.root.add(full.root)
    actor.mesh = full
  }

  /** After glory — demote overflow slots back to lite; budget slots stay full KayKit. */
  private async restoreLiteMesh(actor: Actor): Promise<void> {
    if (!actor.preferLite || actor.mesh.quality === 'low') return

    const pos = actor.mesh.root.position.clone()
    const rotY = actor.mesh.root.rotation.y

    this.root.remove(actor.mesh.root)
    actor.mesh.dispose()

    const lite = await buildCrowdAvatar(
      actor.data.appearance,
      actor.data.displayName,
      'Walk',
      { lite: true },
    )
    lite.root.position.copy(pos)
    lite.root.position.y = 0
    lite.root.scale.setScalar(1)
    lite.root.rotation.set(0, rotY, 0)
    lite.setNameVisible(false)
    this.root.add(lite.root)
    actor.mesh = lite
  }

  /** Flip every idle walker to the current crowd quality mode. */
  private applyCrowdLiteMode(preferLite: boolean): void {
    for (const actor of this.actors.values()) {
      actor.preferLite = preferLite
      if (actor.busy || this.spotlight === actor || this.spotlightPreparing) continue
      if (preferLite && actor.mesh.quality === 'high') {
        void this.restoreLiteMesh(actor)
      } else if (!preferLite && actor.mesh.quality === 'low') {
        void this.ensureFullMesh(actor)
      }
    }
  }

  private crowdShouldBeLite(size: number = this.actors.size): boolean {
    return size >= this.liteCrowdThreshold
  }

  /** Draw spotlight avatar in front of the walking crowd */
  private setGloryDrawOrder(actor: Actor, onTop: boolean): void {
    const root = actor.mesh.root
    if (onTop) {
      this.root.remove(root)
      this.root.add(root)
    }
    root.traverse((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.SkinnedMesh) {
        // Keep normal depth testing — disabling it mangled KayKit skinned meshes.
        // Occlusion vs crowd is solved by gloryZ() sitting in front of all lanes.
        obj.renderOrder = onTop ? 40 : 0
        obj.frustumCulled = true
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
        for (const m of mats) {
          if (!m) continue
          m.depthTest = true
          m.depthWrite = true
          m.needsUpdate = true
        }
      }
      if (obj instanceof THREE.Sprite) {
        obj.renderOrder = onTop ? 50 : 1
        const mat = obj.material as THREE.SpriteMaterial
        mat.depthTest = false
        mat.depthWrite = false
      }
    })
  }

  /** Always in front of every patrol lane (toward the camera at +Z). */
  private gloryZ(): number {
    // Front lite lane ≈ +0.7; pull further toward camera so depth buffer wins
    return 2.85
  }

  /** Keep feet on the bottom edge when glory avatar moves closer in Z */
  private feetYForZ(z: number): number {
    const marginPx = 10
    const targetNdcY = -1 + (2 * marginPx) / Math.max(1, this.height)
    let localY = 0
    for (let i = 0; i < 4; i++) {
      const worldY = this.root.position.y + localY
      const ndc = new THREE.Vector3(0, worldY, z).project(this.camera)
      const err = targetNdcY - ndc.y
      if (Math.abs(err) < 0.002) break
      const dist = Math.max(
        0.5,
        this.camera.position.distanceTo(new THREE.Vector3(0, worldY, z)),
      )
      const vFov = THREE.MathUtils.degToRad(this.camera.fov)
      const visibleH = 2 * Math.tan(vFov / 2) * dist
      localY += (err / 2) * visibleH
    }
    return localY
  }

  private placeSpotlightRoot(
    root: THREE.Object3D,
    originX: number,
    z: number,
    scale: number,
  ): void {
    // Pull inward when growing near the edge so the big avatar stays on screen
    const x = this.glorySafeX(originX, scale, z)
    root.position.x = x
    root.position.z = z
    root.position.y = this.feetYForZ(z)
    root.scale.setScalar(scale)
    this.gloryLight.position.set(x, 4.2, z + 3.5)
    this.gloryLight.target.position.set(x, 0.7, z)
  }

  /** Max |x| so a character of this scale still fits in the camera frustum */
  private glorySafeX(originX: number, scale: number, z: number): number {
    const distance = Math.max(1, this.camera.position.z - z)
    const vFov = THREE.MathUtils.degToRad(this.camera.fov)
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect)
    const halfView = Math.tan(hFov / 2) * distance
    // Approximate half-width of Quaternius figure at current glory scale
    const halfBody = 0.28 * scale
    const maxX = Math.max(0.4, halfView * 0.9 - halfBody)
    return THREE.MathUtils.clamp(originX, -maxX, maxX)
  }

  private async upsertActorAsync(data: CrowdAvatar): Promise<void> {
    const existing = this.actors.get(data.id)
    if (existing) {
      if (existing.data.appearance.characterId === data.appearance.characterId) {
        existing.data = data
        existing.lastActiveAt = Date.now()
        existing.mesh.updateName(data.displayName)
        return
      }
      this.root.remove(existing.mesh.root)
      existing.mesh.dispose()
      const useLite =
        existing.preferLite && this.spotlight !== existing
      const mesh = await buildCrowdAvatar(data.appearance, data.displayName, 'Walk', {
        lite: useLite,
      })
      this.root.add(mesh.root)
      existing.data = data
      existing.lastActiveAt = Date.now()
      existing.mesh = mesh
      mesh.root.position.set(existing.homeX, 0, existing.homeZ)
      return
    }

    const nextSize = this.actors.size + 1
    const preferLite = this.crowdShouldBeLite(nextSize)
    // Crossing into dense mode: demote the whole crowd first
    if (preferLite && !this.crowdShouldBeLite(this.actors.size)) {
      this.applyCrowdLiteMode(true)
    }

    const mesh = await buildCrowdAvatar(data.appearance, data.displayName, 'Walk', {
      lite: preferLite,
    })
    this.root.add(mesh.root)
    const index = this.actors.size
    const actor: Actor = {
      data,
      mesh,
      slot: 0,
      lane: 0,
      dir: index % 2 === 0 ? 1 : -1,
      speed: 0.4 + (index % 5) * 0.06,
      phase: (index / Math.max(1, this.maxAvatars)) * Math.PI * 2,
      homeX: 0,
      homeZ: 0,
      busy: false,
      preferLite,
      lastActiveAt: Date.now(),
      blastVx: 0,
      blastVy: 0,
      blastVz: 0,
      blastSpin: 0,
    }
    this.assignSlot(actor, index, Math.max(index + 1, this.actors.size + 1))
    mesh.root.position.set(actor.homeX, 0, actor.homeZ)
    this.actors.set(data.id, actor)
  }

  private tickSpotlight(dt: number): void {
    const actor = this.spotlight
    if (!actor) return
    this.spotlightT += dt
    const root = actor.mesh.root
    const body = actor.mesh.body
    const gloryScale = 2.4
    const groundY = (body.userData.groundY as number | undefined) ?? 0

    if (this.spotlightPhase === 'approach') {
      const t = Math.min(1, this.spotlightT / 0.4)
      const e = easeOutCubic(t)
      const z = THREE.MathUtils.lerp(this.spotlightOriginZ, this.gloryZ(), e)
      const s = THREE.MathUtils.lerp(1, gloryScale, e)
      this.placeSpotlightRoot(root, this.spotlightOriginX, z, s)
      root.rotation.y = THREE.MathUtils.lerp(root.rotation.y, 0, e)
      this.gloryLight.intensity = THREE.MathUtils.lerp(0, 1.9, e)
      if (t >= 1) {
        this.spotlightT = 0
        if (this.spotlightAction === 'throw_grenade') {
          this.spotlightPhase = 'grenade_arm'
        } else if (this.spotlightAction === 'throw_heart') {
          this.spotlightPhase = 'hearts'
          playQuaterniusIdle(actor.mesh.root)
          this.spawnHearts(actor)
        } else if (this.spotlightAction === 'smash_glass') {
          this.spotlightPhase = 'smash_arm'
        } else {
          this.spotlightPhase = 'windup'
        }
      }
      return
    }

    if (this.spotlightPhase === 'hearts') {
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      // Soft hold while hearts float above the head (~3s)
      if (this.spotlightT >= 3) {
        this.spotlightPhase = 'recover'
        this.spotlightT = 0
      }
      return
    }

    if (this.spotlightPhase === 'smash_arm') {
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      if (this.spotlightT < dt * 1.5) playQuaterniusClip(actor.mesh.root, 'Punch')
      // Lean into the camera for the hit
      const lean = Math.min(1, this.spotlightT / 0.35)
      root.rotation.x = -0.12 * lean
      if (this.spotlightT >= 0.38) {
        this.spotlightPhase = 'smash_break'
        this.spotlightT = 0
        root.rotation.x = 0
        this.spawnGlassBreak()
      }
      return
    }

    if (this.spotlightPhase === 'smash_break') {
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      playQuaterniusIdle(actor.mesh.root)
      // Hold while cracks + shards play out
      if (this.spotlightT >= 2.4) {
        this.spotlightPhase = 'recover'
        this.spotlightT = 0
      }
      return
    }

    if (this.spotlightPhase === 'grenade_arm') {
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      if (this.spotlightT < dt * 1.5) {
        this.attachGrenadeToThrower(actor)
        playQuaterniusClip(root, 'Punch')
      }
      if (this.spotlightT >= 0.45) {
        this.spotlightPhase = 'grenade_fly'
        this.spotlightT = 0
        this.releaseGrenade(actor)
      }
      return
    }

    if (this.spotlightPhase === 'grenade_fly') {
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      const landed = this.tickGrenadeFlight(dt)
      if (landed || this.spotlightT > 1.1) {
        this.spotlightPhase = 'boom'
        this.spotlightT = 0
        this.detonateGrenade()
      }
      return
    }

    if (this.spotlightPhase === 'boom') {
      this.tickCrowdBlast(dt)
      if (this.spotlightT >= 1.35) {
        const finished = this.spotlight
        this.resetCrowdAfterBlast()
        this.gloryLight.intensity = 0
        this.spotlight = null
        this.spotlightAction = 'throw_poop'
        this.spotlightPhase = 'approach'
        if (finished) void this.restoreLiteMesh(finished)
      }
      return
    }

    if (this.spotlightPhase === 'windup') {
      const t = Math.min(1, this.spotlightT / 0.35)
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      if (this.spotlightT < dt * 1.5) playQuaterniusClip(actor.mesh.root, 'Punch')
      if (t >= 1) {
        this.spotlightPhase = 'throw'
        this.spotlightT = 0
        this.spawnPoop(actor)
      }
      return
    }

    if (this.spotlightPhase === 'throw') {
      const t = Math.min(1, this.spotlightT / 0.55)
      this.placeSpotlightRoot(root, this.spotlightOriginX, this.gloryZ(), gloryScale)
      if (t >= 1) {
        this.spotlightPhase = 'recover'
        this.spotlightT = 0
      }
      return
    }

    // recover — shrink back in place, resume patrol from here
    {
      const t = Math.min(1, this.spotlightT / 0.55)
      const e = easeInOut(t)
      const z = THREE.MathUtils.lerp(this.gloryZ(), this.spotlightOriginZ, e)
      const s = THREE.MathUtils.lerp(gloryScale, 1, e)
      this.placeSpotlightRoot(root, this.spotlightOriginX, z, s)
      if (this.spotlightT < dt * 1.5) playQuaterniusWalk(actor.mesh.root)
      body.rotation.x = THREE.MathUtils.lerp(body.rotation.x, 0, e)
      body.rotation.y = THREE.MathUtils.lerp(body.rotation.y, 0, e)
      body.rotation.z = THREE.MathUtils.lerp(body.rotation.z, 0, e)
      body.position.y = THREE.MathUtils.lerp(body.position.y, groundY, e)
      body.position.z = THREE.MathUtils.lerp(body.position.z, 0, e)
      this.gloryLight.intensity = THREE.MathUtils.lerp(1.9, 0, e)
      if (t >= 1) {
        actor.busy = false
        actor.mesh.setNameVisible(false)
        body.rotation.set(0, 0, 0)
        body.position.y = groundY
        body.position.z = 0
        root.scale.setScalar(1)
        root.position.set(this.spotlightOriginX, 0, this.spotlightOriginZ)
        this.setGloryDrawOrder(actor, false)
        actor.homeX = this.spotlightOriginX
        actor.homeZ = this.spotlightOriginZ
        this.gloryLight.intensity = 0
        this.spotlight = null
        this.spotlightAction = 'throw_poop'
        void this.restoreLiteMesh(actor)
      }
    }
  }

  private attachGrenadeToThrower(actor: Actor): void {
    this.clearGrenade()
    const grenade = createGrenadeMesh()
    // Local to thrower root (parent scale already enlarges it)
    grenade.position.set(0.18, 0.42, 0.22)
    actor.mesh.root.add(grenade)
    this.grenade = grenade
  }

  private releaseGrenade(actor: Actor): void {
    if (!this.grenade) return
    const world = new THREE.Vector3()
    this.grenade.getWorldPosition(world)
    actor.mesh.root.remove(this.grenade)
    this.root.add(this.grenade)
    this.root.worldToLocal(world)
    this.grenade.position.copy(world)
    this.grenade.scale.setScalar(1)

    // Arc toward the middle of the walking crowd (in crowd-root space)
    const target = new THREE.Vector3(0, 0.15, this.spotlightOriginZ)
    const to = target.clone().sub(this.grenade.position)
    const flight = 0.55
    this.grenadeVx = to.x / flight
    this.grenadeVy = to.y / flight + 4.2
    this.grenadeVz = to.z / flight
  }

  private tickGrenadeFlight(dt: number): boolean {
    if (!this.grenade) return true
    this.grenadeVy -= 14 * dt
    this.grenade.position.x += this.grenadeVx * dt
    this.grenade.position.y += this.grenadeVy * dt
    this.grenade.position.z += this.grenadeVz * dt
    this.grenade.rotation.x += dt * 8
    this.grenade.rotation.z += dt * 6
    return this.grenade.position.y <= 0.08
  }

  private detonateGrenade(): void {
    const blastAt = this.grenade
      ? this.grenade.position.clone()
      : new THREE.Vector3(0, 0.2, this.spotlightOriginZ)
    this.clearGrenade()
    this.spawnExplosion(blastAt)
    void this.playCrowdSfx(SOUND_GRENADE, 0.9)

    for (const actor of this.actors.values()) {
      actor.busy = true
      actor.mesh.setNameVisible(false)
      const root = actor.mesh.root
      const dx = root.position.x - blastAt.x
      const dz = root.position.z - blastAt.z
      const dist = Math.max(0.15, Math.hypot(dx, dz))
      const force = 6.5 / dist
      actor.blastVx = (dx / dist) * force + (Math.random() - 0.5) * 1.5
      actor.blastVy = 3.2 + Math.random() * 2.8
      actor.blastVz = (dz / dist) * force * 0.4 + (Math.random() - 0.5)
      actor.blastSpin = (Math.random() - 0.5) * 12
      playQuaterniusClip(root, 'Jump')
    }
    this.gloryLight.intensity = 3.2
  }

  private tickCrowdBlast(dt: number): void {
    for (const actor of this.actors.values()) {
      const root = actor.mesh.root
      actor.blastVy -= 12 * dt
      root.position.x += actor.blastVx * dt
      root.position.y += actor.blastVy * dt
      root.position.z += actor.blastVz * dt
      root.rotation.z += actor.blastSpin * dt
      root.rotation.x += actor.blastSpin * 0.4 * dt
      // Squash / puff while flying
      const puff = 1 + Math.min(1.2, this.spotlightT) * 0.35
      root.scale.setScalar(Math.max(0.2, puff * (1 - this.spotlightT * 0.35)))
    }
    this.gloryLight.intensity = THREE.MathUtils.lerp(3.2, 0.2, Math.min(1, this.spotlightT / 1.2))
  }

  private resetCrowdAfterBlast(): void {
    this.clearBoomBits()
    const list = [...this.actors.values()]
    list.forEach((actor, index) => {
      this.assignSlot(actor, index, list.length)
      const groundY = (actor.mesh.body.userData.groundY as number | undefined) ?? 0
      actor.mesh.body.rotation.set(0, 0, 0)
      actor.mesh.body.position.y = groundY
      actor.mesh.body.position.z = 0
      actor.mesh.root.rotation.set(0, actor.dir > 0 ? Math.PI / 2 : -Math.PI / 2, 0)
      actor.mesh.root.scale.setScalar(1)
      actor.mesh.root.position.set(actor.homeX, 0, actor.homeZ)
      actor.blastVx = 0
      actor.blastVy = 0
      actor.blastVz = 0
      actor.blastSpin = 0
      actor.busy = false
      actor.mesh.setNameVisible(false)
      this.setGloryDrawOrder(actor, false)
      playQuaterniusWalk(actor.mesh.root)
    })
  }

  private spawnExplosion(at: THREE.Vector3): void {
    for (let i = 0; i < 18; i++) {
      const color = i % 3 === 0 ? '#ffcc33' : i % 3 === 1 ? '#ff6622' : '#ffeebb'
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.08 + Math.random() * 0.12, 6, 6),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 }),
      )
      mesh.position.copy(at)
      mesh.renderOrder = 70
      this.root.add(mesh)
      const speed = 2 + Math.random() * 5
      const dir = new THREE.Vector3(
        Math.random() - 0.5,
        Math.random() * 0.9,
        Math.random() - 0.5,
      ).normalize()
      const life = 0.45 + Math.random() * 0.4
      this.boomBits.push({
        mesh,
        vx: dir.x * speed,
        vy: dir.y * speed + 1.5,
        vz: dir.z * speed,
        life,
        maxLife: life,
      })
    }
  }

  private tickBoomBits(dt: number): void {
    for (let i = this.boomBits.length - 1; i >= 0; i--) {
      const b = this.boomBits[i]
      if (!b) continue
      b.life -= dt
      b.vy -= 6 * dt
      b.mesh.position.x += b.vx * dt
      b.mesh.position.y += b.vy * dt
      b.mesh.position.z += b.vz * dt
      const k = Math.max(0, b.life / b.maxLife)
      b.mesh.scale.setScalar(0.6 + (1 - k) * 1.8)
      ;(b.mesh.material as THREE.MeshBasicMaterial).opacity = k
      if (b.life <= 0) {
        this.root.remove(b.mesh)
        b.mesh.geometry.dispose()
        ;(b.mesh.material as THREE.Material).dispose()
        this.boomBits.splice(i, 1)
      }
    }
  }

  private clearGrenade(): void {
    if (!this.grenade) return
    this.grenade.removeFromParent()
    disposeObject3D(this.grenade)
    this.grenade = null
  }

  private clearBoomBits(): void {
    for (const b of this.boomBits) {
      this.root.remove(b.mesh)
      b.mesh.geometry.dispose()
      ;(b.mesh.material as THREE.Material).dispose()
    }
    this.boomBits.length = 0
  }

  /** Small hearts float just above the nick / head — no throw, stay on-screen. */
  private spawnHearts(actor: Actor): void {
    const count = 6
    const nick = actor.mesh.nameSprite
    const baseY = nick.position.y + 0.15
    for (let i = 0; i < count; i++) {
      const mesh = createHeartMesh(
        i % 3 === 0 ? '#ff6b8a' : i % 3 === 1 ? '#ff4d6d' : '#ff8fab',
      )
      // Fan around the head in a loose arc — not one pile
      const angle = (i / count) * Math.PI * 1.4 - Math.PI * 0.7 + (Math.random() - 0.5) * 0.25
      const radius = 0.28 + Math.random() * 0.35
      mesh.position.set(
        Math.sin(angle) * radius,
        baseY + 0.05 + Math.random() * 0.35 + (i % 2) * 0.12,
        nick.position.z + Math.cos(angle) * radius * 0.25,
      )
      const bodyScale = Math.max(0.01, actor.mesh.body.scale.x)
      const baseScale = (0.32 + Math.random() * 0.12) / bodyScale
      mesh.scale.setScalar(0.01)
      actor.mesh.body.add(mesh)

      this.hearts.push({
        mesh,
        vx: Math.sin(angle) * (0.04 + Math.random() * 0.05),
        vy: 0.02 + Math.random() * 0.025,
        vz: Math.cos(angle) * (0.02 + Math.random() * 0.03),
        life: -i * 0.18,
        maxLife: 3,
        spin: (Math.random() - 0.5) * 1.6,
        sway: angle + Math.random(),
        baseScale,
      })
    }
  }

  private tickHearts(dt: number): void {
    for (let i = this.hearts.length - 1; i >= 0; i--) {
      const h = this.hearts[i]
      if (!h) continue
      h.life += dt
      if (h.life < 0) continue

      // Soft bob + slight outward drift so they don’t stay piled
      h.mesh.position.x += h.vx * dt + Math.sin(h.life * 2.8 + h.sway) * 0.1 * dt
      h.mesh.position.y += h.vy * dt + Math.sin(h.life * 3.4 + h.sway) * 0.05 * dt
      h.mesh.position.z += h.vz * dt
      h.vx *= 1 + 0.15 * dt
      h.vz *= 1 + 0.15 * dt
      h.mesh.rotation.z += h.spin * dt

      const u = Math.min(1, h.life / h.maxLife)
      const pop = u < 0.12 ? u / 0.12 : 1
      const fade = u > 0.72 ? 1 - (u - 0.72) / 0.28 : 1
      h.mesh.scale.setScalar(h.baseScale * pop * Math.max(0.25, fade))
      setHeartOpacity(h.mesh, Math.max(0, fade))

      if (h.life >= h.maxLife) {
        h.mesh.removeFromParent()
        disposeObject3D(h.mesh)
        this.hearts.splice(i, 1)
      }
    }
  }

  /** Punch impact: cracks + shards on the overlay “glass”. */
  private spawnGlassBreak(): void {
    this.clearGlassFx()

    // Sync SFX with crack/shard spawn (end of punch lean ~0.38s)
    this.playCrowdSfx(SOUND_SMASH_GLASS, 0.85)

    const hitNdcX = (Math.random() - 0.5) * 0.2
    const hitNdcY = 0.05 + Math.random() * 0.15
    const hit = this.glassHitPoint(hitNdcX, hitNdcY)

    // Impact flash
    const flashMat = new THREE.MeshBasicMaterial({
      color: '#e8f4ff',
      transparent: true,
      opacity: 0.85,
      depthTest: false,
      depthWrite: false,
    })
    const flash = new THREE.Mesh(new THREE.CircleGeometry(0.18, 20), flashMat)
    flash.position.copy(hit)
    flash.lookAt(this.camera.position)
    flash.renderOrder = 90
    this.scene.add(flash)
    this.glassFlash = flash

    // Main spider cracks — jagged polylines, not straight bars
    const mainCount = 9
    for (let i = 0; i < mainCount; i++) {
      const angle = (i / mainCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.4
      const len = 0.45 + Math.random() * 0.7
      this.addJaggedCrack({
        origin: hit,
        startAngle: angle,
        totalLen: len,
        segments: 5 + Math.floor(Math.random() * 4),
        width: 0.01 + Math.random() * 0.006,
        color: i % 2 === 0 ? '#a8c0d4' : '#d5e5f2',
        maxLife: 2.6,
        branchChance: 0.45,
      })
    }

    // Shorter secondary cracks near impact
    for (let i = 0; i < 6; i++) {
      const angle = Math.random() * Math.PI * 2
      this.addJaggedCrack({
        origin: hit.clone().add(
          new THREE.Vector3(
            (Math.random() - 0.5) * 0.2,
            (Math.random() - 0.5) * 0.15,
            0,
          ),
        ),
        startAngle: angle,
        totalLen: 0.18 + Math.random() * 0.28,
        segments: 3 + Math.floor(Math.random() * 3),
        width: 0.006 + Math.random() * 0.004,
        color: '#c2d6e6',
        maxLife: 2.3,
        branchChance: 0.2,
      })
    }

    // Flying shards (irregular triangles)
    for (let i = 0; i < 16; i++) {
      const shard = createGlassShardMesh()
      shard.position.copy(hit)
      shard.position.x += (Math.random() - 0.5) * 0.1
      shard.position.y += (Math.random() - 0.5) * 0.1
      shard.lookAt(this.camera.position)
      this.scene.add(shard)

      this.glassShards.push({
        mesh: shard,
        vx: (Math.random() - 0.5) * 2.2,
        vy: 0.4 + Math.random() * 1.6,
        vz: (Math.random() - 0.5) * 0.6 - 0.3,
        spinX: (Math.random() - 0.5) * 10,
        spinY: (Math.random() - 0.5) * 10,
        spinZ: (Math.random() - 0.5) * 12,
        life: 0,
        maxLife: 1.4 + Math.random() * 0.9,
      })
    }
  }

  /**
   * One jagged crack as short segments with random turns (+ optional branches).
   * Built in the camera-facing glass plane so it reads as monitor damage.
   */
  private addJaggedCrack(opts: {
    origin: THREE.Vector3
    startAngle: number
    totalLen: number
    segments: number
    width: number
    color: string
    maxLife: number
    branchChance: number
  }): void {
    const { origin, startAngle, totalLen, segments, width, color, maxLife, branchChance } =
      opts

    const toCam = new THREE.Vector3()
      .subVectors(this.camera.position, origin)
      .normalize()
    const worldUp = new THREE.Vector3(0, 1, 0)
    const right = new THREE.Vector3().crossVectors(worldUp, toCam).normalize()
    const up = new THREE.Vector3().crossVectors(toCam, right).normalize()

    const group = new THREE.Group()
    let angle = startAngle
    let pos = origin.clone()
    let remaining = totalLen
    const segTarget = totalLen / Math.max(1, segments)

    for (let s = 0; s < segments && remaining > 0.02; s++) {
      // Random walk — real cracks kink, they don’t go straight
      angle += (Math.random() - 0.5) * 1.05
      if (Math.random() < 0.2) angle += (Math.random() > 0.5 ? 1 : -1) * 0.55

      const segLen = Math.min(remaining, segTarget * (0.55 + Math.random() * 0.9))
      const dir = right
        .clone()
        .multiplyScalar(Math.cos(angle))
        .addScaledVector(up, Math.sin(angle))
        .normalize()
      const mid = pos.clone().addScaledVector(dir, segLen * 0.5)

      const taper = 1 - (s / Math.max(1, segments - 1)) * 0.65
      const opacity = 0.55 + Math.random() * 0.4
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      })
      const seg = new THREE.Mesh(
        new THREE.PlaneGeometry(width * taper * (0.7 + Math.random() * 0.5), segLen),
        mat,
      )
      seg.userData.baseOpacity = opacity
      seg.position.copy(mid)
      seg.lookAt(this.camera.position)
      const localAngle = Math.atan2(dir.dot(up), dir.dot(right))
      seg.rotateZ(localAngle - Math.PI / 2)
      seg.position.addScaledVector(toCam, 0.002 * s)
      seg.renderOrder = 88
      group.add(seg)

      pos.addScaledVector(dir, segLen)
      remaining -= segLen

      // Fork: short side branch
      if (branchChance > 0 && s >= 1 && Math.random() < branchChance) {
        this.addJaggedCrack({
          origin: pos.clone(),
          startAngle: angle + (Math.random() > 0.5 ? 0.7 : -0.7) + (Math.random() - 0.5) * 0.4,
          totalLen: totalLen * (0.22 + Math.random() * 0.2),
          segments: 2 + Math.floor(Math.random() * 2),
          width: width * 0.65,
          color,
          maxLife,
          branchChance: 0,
        })
      }
    }

    this.scene.add(group)
    this.glassCracks.push({ mesh: group, life: 0, maxLife })
  }

  private tickGlassFx(dt: number): void {
    if (this.glassFlash) {
      const mat = this.glassFlash.material as THREE.MeshBasicMaterial
      mat.opacity = Math.max(0, mat.opacity - dt * 2.8)
      this.glassFlash.scale.multiplyScalar(1 + dt * 3.5)
      if (mat.opacity <= 0.02) {
        this.scene.remove(this.glassFlash)
        this.glassFlash.geometry.dispose()
        mat.dispose()
        this.glassFlash = null
      }
    }

    for (let i = this.glassCracks.length - 1; i >= 0; i--) {
      const c = this.glassCracks[i]
      if (!c) continue
      c.life += dt
      const u = Math.min(1, c.life / c.maxLife)
      const fade = u > 0.55 ? 1 - (u - 0.55) / 0.45 : 1
      c.mesh.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return
        const m = obj.material as THREE.MeshBasicMaterial
        if (!m) return
        const base = (obj.userData.baseOpacity as number | undefined) ?? 0.8
        m.opacity = base * fade
      })
      if (c.life >= c.maxLife) {
        this.scene.remove(c.mesh)
        disposeObject3D(c.mesh)
        this.glassCracks.splice(i, 1)
      }
    }

    for (let i = this.glassShards.length - 1; i >= 0; i--) {
      const s = this.glassShards[i]
      if (!s) continue
      s.life += dt
      s.vy -= 4.5 * dt
      s.mesh.position.x += s.vx * dt
      s.mesh.position.y += s.vy * dt
      s.mesh.position.z += s.vz * dt
      s.mesh.rotation.x += s.spinX * dt
      s.mesh.rotation.y += s.spinY * dt
      s.mesh.rotation.z += s.spinZ * dt
      const u = Math.min(1, s.life / s.maxLife)
      ;(s.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.7 * (1 - u))
      if (s.life >= s.maxLife) {
        this.scene.remove(s.mesh)
        s.mesh.geometry.dispose()
        ;(s.mesh.material as THREE.Material).dispose()
        this.glassShards.splice(i, 1)
      }
    }
  }

  private clearGlassFx(): void {
    if (this.glassFlash) {
      this.scene.remove(this.glassFlash)
      this.glassFlash.geometry.dispose()
      ;(this.glassFlash.material as THREE.Material).dispose()
      this.glassFlash = null
    }
    for (const c of this.glassCracks) {
      this.scene.remove(c.mesh)
      disposeObject3D(c.mesh)
    }
    this.glassCracks.length = 0
    for (const s of this.glassShards) {
      this.scene.remove(s.mesh)
      s.mesh.geometry.dispose()
      ;(s.mesh.material as THREE.Material).dispose()
    }
    this.glassShards.length = 0
  }

  private spawnPoop(actor: Actor): void {
    const mesh = createPoopMesh()
    const start = new THREE.Vector3()
    actor.mesh.root.getWorldPosition(start)
    start.y += 0.7 * actor.mesh.root.scale.x
    start.z += 0.2

    // Hit somewhere in the upper-middle — local smear, not a full-screen wipe
    const ndcX = (Math.random() - 0.5) * 0.45
    const ndcY = 0.28 + Math.random() * 0.25
    const target = this.glassHitPoint(ndcX, ndcY)

    mesh.position.copy(start)
    mesh.scale.setScalar(1.25)
    this.scene.add(mesh)
    setPoopDepthOverlay(mesh, false)

    const toTarget = new THREE.Vector3().subVectors(target, start)
    const dist = Math.max(0.5, toTarget.length())
    const flight = 0.45
    const dir = toTarget.normalize()
    this.poops.push({
      mesh,
      vx: dir.x * (dist / flight),
      vy: dir.y * (dist / flight) + 2.2,
      vz: dir.z * (dist / flight),
      life: flight + 0.35,
      phase: 'fly',
      dripT: 0,
      splatScaleX: 1,
      splatScaleY: 1,
      streaks: [],
      target,
      ndcX,
      ndcY,
      ndcVx: 0,
      ndcVy: 0,
    })
  }

  /** World point on the overlay “glass” (fixed distance along the camera ray). */
  private glassHitPoint(ndcX: number, ndcY: number): THREE.Vector3 {
    const dist = 1.2
    const worldDir = new THREE.Vector3(ndcX, ndcY, 0.5)
      .unproject(this.camera)
      .sub(this.camera.position)
      .normalize()
    return this.camera.position.clone().addScaledVector(worldDir, dist)
  }

  private placeOnGlass(obj: THREE.Object3D, ndcX: number, ndcY: number): void {
    obj.position.copy(this.glassHitPoint(ndcX, ndcY))
  }

  private tickPoops(dt: number): void {
    for (let i = this.poops.length - 1; i >= 0; i--) {
      const p = this.poops[i]
      if (!p) continue

      if (p.phase === 'splat') {
        this.tickPoopSplat(p, dt)
        continue
      }
      if (p.phase === 'drip') {
        this.tickPoopDrip(p, dt, i)
        continue
      }

      p.life -= dt
      p.vy -= 8.5 * dt
      p.mesh.position.x += p.vx * dt
      p.mesh.position.y += p.vy * dt
      p.mesh.position.z += p.vz * dt
      p.mesh.rotation.x += dt * 9
      p.mesh.rotation.z += dt * 6
      p.mesh.rotation.y += dt * 3.5

      const toGlass = p.mesh.position.distanceTo(p.target)
      const nearCam = p.mesh.position.distanceTo(this.camera.position) < 1.55
      if (toGlass < 0.65 || nearCam || p.life <= 0) {
        this.beginPoopSplat(p)
      }
    }
  }

  private beginPoopSplat(p: PoopProjectile): void {
    this.placeOnGlass(p.mesh, p.ndcX, p.ndcY)
    p.phase = 'splat'
    p.dripT = 0
    p.vx = 0
    p.vy = 0
    p.vz = 0
    // Hit the glass — play splat SFX with the impact morph
    this.playCrowdSfx(SOUND_POOP_SPLAT, 0.9)
    // Compact splat — reads as a blob on glass, not a screen wipe
    p.splatScaleX = 1.15 + Math.random() * 0.25
    p.splatScaleY = 0.85 + Math.random() * 0.2
    p.mesh.rotation.set(0.12, Math.random() * Math.PI, (Math.random() - 0.5) * 0.25)
    setPoopDepthOverlay(p.mesh, true)
    setPoopOpacity(p.mesh, 1)
    p.mesh.scale.set(p.splatScaleX * 0.45, p.splatScaleY * 1.35, 0.35)
  }

  private tickPoopSplat(p: PoopProjectile, dt: number): void {
    p.dripT += dt
    const t = Math.min(1, p.dripT / 0.16)
    const e = 1 - Math.pow(1 - t, 3)
    p.mesh.scale.x = THREE.MathUtils.lerp(p.splatScaleX * 0.45, p.splatScaleX, e)
    p.mesh.scale.y = THREE.MathUtils.lerp(p.splatScaleY * 1.35, p.splatScaleY, e)
    p.mesh.scale.z = THREE.MathUtils.lerp(0.35, 0.42, e)
    this.placeOnGlass(p.mesh, p.ndcX, p.ndcY)

    if (t >= 1) {
      p.phase = 'drip'
      p.dripT = 0
      p.ndcVx = (Math.random() - 0.5) * 0.03
      p.ndcVy = -0.12
      this.spawnGlassStreaks(p)
    }
  }

  private tickPoopDrip(p: PoopProjectile, dt: number, index: number): void {
    p.dripT += dt
    // Viscous slide — stays near impact, slowly creeps down
    p.ndcVy = THREE.MathUtils.lerp(p.ndcVy, -0.22, 1 - Math.pow(0.05, dt))
    p.ndcX += p.ndcVx * dt
    p.ndcY += p.ndcVy * dt
    this.placeOnGlass(p.mesh, p.ndcX, p.ndcY)

    // Mild melt, not a giant smear
    const stretch = 1 + p.dripT * 0.55
    p.mesh.scale.x = p.splatScaleX / Math.sqrt(stretch)
    p.mesh.scale.y = p.splatScaleY * stretch
    p.mesh.scale.z = 0.4
    const fade = Math.max(0, 1 - p.dripT / 2.2)
    setPoopOpacity(p.mesh, fade)

    this.tickPoopStreaks(p, dt)

    if (p.dripT > 2.4 || (fade <= 0.02 && p.streaks.length === 0)) {
      this.disposePoop(p)
      this.poops.splice(index, 1)
    }
  }

  private spawnGlassStreaks(p: PoopProjectile): void {
    // A few local drips under the splat — not a fan across the overlay
    const count = 3 + Math.floor(Math.random() * 2)
    for (let i = 0; i < count; i++) {
      const ndcX = p.ndcX + (Math.random() - 0.5) * 0.08
      const ndcY = p.ndcY - 0.02 - Math.random() * 0.04
      const size = 0.028 + Math.random() * 0.022
      const mat = new THREE.MeshBasicMaterial({
        color: i % 2 === 0 ? '#5c3317' : '#7a4a28',
        transparent: true,
        opacity: 0.9,
        depthTest: false,
        depthWrite: false,
      })
      const drop = new THREE.Mesh(new THREE.SphereGeometry(size, 7, 6), mat)
      this.placeOnGlass(drop, ndcX, ndcY)
      const baseScaleX = 0.55 + Math.random() * 0.25
      const baseScaleY = 1.1 + Math.random() * 0.5
      drop.scale.set(baseScaleX, baseScaleY, 0.28)
      drop.renderOrder = 70
      this.scene.add(drop)

      p.streaks.push({
        mesh: drop,
        ndcX,
        ndcY,
        vxNdc: (Math.random() - 0.5) * 0.025,
        vyNdc: -0.1 - Math.random() * 0.12,
        delay: 0.08 + Math.random() * 0.35,
        age: 0,
        maxLife: 1.4 + Math.random() * 0.7,
        baseScaleX,
        baseScaleY,
      })
    }
  }

  private tickPoopStreaks(p: PoopProjectile, dt: number): void {
    for (let i = p.streaks.length - 1; i >= 0; i--) {
      const s = p.streaks[i]
      if (!s) continue
      s.age += dt
      if (s.age < s.delay) continue

      const alive = s.age - s.delay
      // Slow drip with slight wobble
      s.vyNdc -= 0.18 * dt
      s.ndcX += s.vxNdc * dt + Math.sin(alive * 6 + i) * 0.004 * dt
      s.ndcY += s.vyNdc * dt
      this.placeOnGlass(s.mesh, s.ndcX, s.ndcY)

      const stretch = 1 + alive * 1.4
      s.mesh.scale.x = Math.max(0.28, s.baseScaleX / Math.sqrt(stretch))
      s.mesh.scale.y = s.baseScaleY * stretch
      ;(s.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(
        0,
        0.9 * (1 - alive / s.maxLife),
      )

      // Short local run — fade out before painting half the overlay
      if (alive > s.maxLife || s.ndcY < -0.92) {
        this.scene.remove(s.mesh)
        s.mesh.geometry.dispose()
        ;(s.mesh.material as THREE.Material).dispose()
        p.streaks.splice(i, 1)
      }
    }
  }

  private disposePoop(p: PoopProjectile): void {
    for (const s of p.streaks) {
      this.scene.remove(s.mesh)
      s.mesh.geometry.dispose()
      ;(s.mesh.material as THREE.Material).dispose()
    }
    p.streaks.length = 0
    this.scene.remove(p.mesh)
    disposeObject3D(p.mesh)
  }

  private tickPatrol(dt: number): void {
    const span = this.getPatrolSpan()
    const t = this.clock.elapsedTime

    for (const actor of this.actors.values()) {
      if (actor.busy) continue
      const root = actor.mesh.root
      const mesh = actor.mesh

      root.visible = true
      // Keep upright — only yaw for facing
      root.rotation.x = 0
      root.rotation.z = 0

      // Independent patrol — clamp every frame so they never drift off-screen
      root.position.x += actor.dir * actor.speed * dt
      root.position.z = actor.homeZ
      root.position.x = THREE.MathUtils.clamp(root.position.x, -span, span)

      if (root.position.x >= span - 0.001) {
        root.position.x = span
        actor.dir = -1
        root.rotation.y = -Math.PI / 2
      } else if (root.position.x <= -span + 0.001) {
        root.position.x = -span
        actor.dir = 1
        root.rotation.y = Math.PI / 2
      } else {
        const targetYaw = actor.dir > 0 ? Math.PI / 2 : -Math.PI / 2
        root.rotation.y = THREE.MathUtils.lerp(root.rotation.y, targetYaw, 1 - Math.pow(0.0008, dt))
      }

      // Procedural walk for lite/low meshes (no AnimationMixer)
      if (mesh.quality === 'low') {
        const walk = t * (4.5 + actor.speed) + actor.phase
        const swing = Math.sin(walk) * 0.55
        mesh.leftLeg.rotation.x = swing
        mesh.rightLeg.rotation.x = -swing
        mesh.leftArm.rotation.x = -swing * 0.7
        mesh.rightArm.rotation.x = swing * 0.7
        mesh.body.position.y =
          ((mesh.body.userData.groundY as number | undefined) ?? 0) +
          Math.abs(Math.sin(walk)) * 0.03
      }
    }
  }
}

/** Stylized ❤️ from two blobs + tip */
function createHeartMesh(color: string): THREE.Group {
  const g = new THREE.Group()
  const mat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 1,
    depthTest: false,
    depthWrite: false,
  })
  const left = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), mat)
  left.position.set(-0.038, 0.028, 0)
  const right = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), mat.clone())
  right.position.set(0.038, 0.028, 0)
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.078, 0.11, 8), mat.clone())
  tip.rotation.z = Math.PI
  tip.position.set(0, -0.045, 0)
  g.add(left, right, tip)
  g.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.renderOrder = 72
      o.frustumCulled = false
    }
  })
  return g
}

function setHeartOpacity(root: THREE.Object3D, opacity: number): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) {
      if (!m) continue
      m.transparent = true
      m.opacity = opacity
      m.needsUpdate = true
    }
  })
}

/** Cartoon 💩 — stacked blobs with a little tip */
function createPoopMesh(): THREE.Group {
  const g = new THREE.Group()
  const mat = new THREE.MeshBasicMaterial({
    color: '#5e3518',
    transparent: true,
    opacity: 1,
    depthTest: true,
    depthWrite: true,
  })
  const midMat = mat.clone()
  midMat.color = new THREE.Color('#6b3f1d')
  const tipMat = mat.clone()
  tipMat.color = new THREE.Color('#8a5530')

  const base = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), mat)
  base.scale.set(1.3, 0.75, 1.2)
  base.position.y = 0

  const mid = new THREE.Mesh(new THREE.SphereGeometry(0.095, 10, 8), midMat)
  mid.scale.set(1.15, 0.8, 1.08)
  mid.position.set(0.01, 0.11, 0)

  const top = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 7), midMat)
  top.scale.set(1.08, 0.88, 1)
  top.position.set(-0.01, 0.21, 0.01)

  const tip = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 6), tipMat)
  tip.position.set(0.028, 0.29, 0)

  g.add(base, mid, top, tip)
  g.traverse((o) => {
    if (o instanceof THREE.Mesh) o.renderOrder = 55
  })
  return g
}

function setPoopOpacity(root: THREE.Object3D, opacity: number): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) {
      if (!m) continue
      m.transparent = true
      m.opacity = opacity
      m.needsUpdate = true
    }
  })
}

function setPoopDepthOverlay(root: THREE.Object3D, onGlass: boolean): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    o.renderOrder = onGlass ? 68 : 55
    o.frustumCulled = !onGlass
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) {
      if (!m) continue
      m.depthTest = !onGlass
      m.depthWrite = false
      m.needsUpdate = true
    }
  })
}

function disposeObject3D(root: THREE.Object3D): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return
    o.geometry.dispose()
    const mats = Array.isArray(o.material) ? o.material : [o.material]
    for (const m of mats) m?.dispose()
  })
}

/** Irregular glass shard (triangle / quad plane). */
function createGlassShardMesh(): THREE.Mesh {
  const geo = new THREE.BufferGeometry()
  const s = 0.04 + Math.random() * 0.07
  const verts =
    Math.random() > 0.4
      ? new Float32Array([
          -s * (0.4 + Math.random() * 0.5),
          -s * (0.3 + Math.random() * 0.4),
          0,
          s * (0.5 + Math.random() * 0.5),
          -s * (0.2 + Math.random() * 0.5),
          0,
          (Math.random() - 0.5) * s * 0.6,
          s * (0.5 + Math.random() * 0.6),
          0,
        ])
      : new Float32Array([
          -s * 0.6,
          -s * 0.4,
          0,
          s * 0.5,
          -s * 0.5,
          0,
          s * 0.4,
          s * 0.5,
          0,
          -s * 0.3,
          s * 0.45,
          0,
        ])
  geo.setAttribute('position', new THREE.BufferAttribute(verts, 3))
  if (verts.length === 12) geo.setIndex([0, 1, 2, 0, 2, 3])
  geo.computeVertexNormals()

  return new THREE.Mesh(
    geo,
    new THREE.MeshBasicMaterial({
      color: Math.random() > 0.5 ? '#dceaf5' : '#b7cfe0',
      transparent: true,
      opacity: 0.7,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  )
}

/** Simple cartoon grenade */
function createGrenadeMesh(): THREE.Group {
  const g = new THREE.Group()
  const bodyMat = new THREE.MeshStandardMaterial({
    color: '#2f6b2f',
    roughness: 0.7,
    metalness: 0.2,
  })
  const metal = new THREE.MeshStandardMaterial({
    color: '#c0c4c8',
    roughness: 0.35,
    metalness: 0.8,
  })
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 10), bodyMat)
  body.scale.set(1, 1.15, 1)
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.04, 0.05, 8), metal)
  neck.position.y = 0.1
  const pin = new THREE.Mesh(new THREE.TorusGeometry(0.03, 0.008, 6, 10), metal)
  pin.position.set(0.03, 0.13, 0)
  pin.rotation.y = Math.PI / 2
  g.add(body, neck, pin)
  g.traverse((o) => {
    if (o instanceof THREE.Mesh) o.renderOrder = 56
  })
  return g
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
}
